import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import type { ContentItem } from '../../api/contentApi'

/* eslint-disable @typescript-eslint/no-explicit-any */
type Payload = Record<string, any>

interface RoomEditorProps {
  item: ContentItem
  onSave: (payload: Payload) => void
  saving?: boolean
}

export function RoomEditor({ item, onSave, saving }: RoomEditorProps) {
  const [p, setP] = useState<Payload>((item.payload as Payload) || {})
  useEffect(() => {
    setP((item.payload as Payload) || {})
  }, [item.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: string, v: any) => setP((prev) => ({ ...prev, [k]: v }))
  const palette: Payload = p.palette || {}
  const setPal = (k: string, v: string) => setP((prev) => ({ ...prev, palette: { ...(prev.palette || {}), [k]: v } }))
  const tags: string[] = Array.isArray(p.taxonomy_tags) ? p.taxonomy_tags : []
  const concepts: string[] = Array.isArray(p.concepts) ? p.concepts : []
  const setMeta: Payload | null = p.set_meta || null

  return (
    <div className="space-y-5">
      {p.hero_image_url ? (
        <img src={String(p.hero_image_url)} alt="" className="w-full h-48 object-cover rounded-md border" />
      ) : null}

      <div className="grid grid-cols-2 gap-4">
        <Field label="Subtitle"><Input value={String(p.subtitle || '')} onChange={(e) => set('subtitle', e.target.value)} /></Field>
        <Field label="Mood"><Input value={String(p.mood || '')} onChange={(e) => set('mood', e.target.value)} /></Field>
        <Field label="Primary room"><Input value={String(p.primary_room || '')} onChange={(e) => set('primary_room', e.target.value)} /></Field>
        <Field label="Color temperature"><Input value={String(p.color_temperature || '')} onChange={(e) => set('color_temperature', e.target.value)} /></Field>
        <Field label="Energy level"><Input value={String(p.energy_level || '')} onChange={(e) => set('energy_level', e.target.value)} /></Field>
        <Field label="Formality"><Input value={String(p.formality || '')} onChange={(e) => set('formality', e.target.value)} /></Field>
        <Field label="Seasonality"><Input value={String(p.seasonality || '')} onChange={(e) => set('seasonality', e.target.value)} /></Field>
      </div>

      <Field label="Narrative"><Textarea rows={3} value={String(p.narrative || '')} onChange={(e) => set('narrative', e.target.value)} /></Field>
      <Field label="Curatorial note"><Textarea rows={2} value={String(p.curatorial_note || '')} onChange={(e) => set('curatorial_note', e.target.value)} /></Field>

      <Field label="Palette">
        <div className="flex gap-4 flex-wrap">
          {['primary', 'secondary', 'accent'].map((k) => (
            <div key={k} className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full border" style={{ background: palette[k] || '#ffffff' }} />
              <Input className="w-28" value={String(palette[k] || '')} onChange={(e) => setPal(k, e.target.value)} placeholder={k} />
            </div>
          ))}
        </div>
      </Field>

      <Field label="Taxonomy tags (comma-separated)">
        <Input
          value={tags.join(', ')}
          onChange={(e) => set('taxonomy_tags', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
        />
      </Field>

      {concepts.length > 0 && (
        <Field label="Graph concepts (read-only)">
          <div className="flex flex-wrap gap-1.5">
            {concepts.map((c) => (
              <span key={c} className="text-xs bg-muted rounded-full px-2 py-0.5">{c}</span>
            ))}
          </div>
        </Field>
      )}

      {setMeta && (
        <Field label="Gallery-wall set (read-only)">
          <p className="text-sm text-muted-foreground">
            {setMeta.name} · {setMeta.piece_count} pieces · ${setMeta.price}
            {setMeta.savings ? ` · save $${setMeta.savings}` : ''}
          </p>
        </Field>
      )}

      <div className="flex justify-end">
        <Button onClick={() => onSave(p)} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  )
}
